import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import Header from './components/Header';
import Hero from './components/Hero';
import AIChat from './components/AIChat';
import Footer from './components/Footer';
import SectionHeader from './design/SectionHeader';
import AuthCallback from './pages/AuthCallback';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import MobileBottomNav from './components/MobileBottomNav';
import FloatingAIWidget from './components/FloatingAIWidget';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { getPaymentStatus } from './utils/api';
import { trackEvent } from './utils/analytics';
import { getPageMetadata, shouldIndexHost } from './utils/pageMetadata';

// Lazy-loaded sections — these never render on initial paint (homepage
// shows Hero + AIChat first). Splitting them out trims the initial bundle
// by ~30% and lets the LCP land before the user scrolls. Each chunk is
// fetched on demand via Webpack's dynamic-import code-splitting.
const HiddenGemOfTheDay = lazy(() => import('./components/discovery/HiddenGemOfTheDay'));
const TravelServicesSection = lazy(() => import('./components/TravelServicesSection'));
const TravelBookingSection = lazy(() => import('./components/TravelBookingSection'));
// PartnerLogosRow — no longer needed on the homepage; the Hero now
// carries its own partner strip. Keep the file for any /partners route.
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const HomepageFAQ = lazy(() => import('./components/HomepageFAQ'));
const JarvisRareFinds = lazy(() => import('./components/discovery/JarvisRareFinds'));
const DiscoveryDeals = lazy(() => import('./components/discovery/DiscoveryDeals'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const TripComparison = lazy(() => import('./components/TripComparison'));
const HotelSearch = lazy(() => import('./components/HotelSearch'));
const FlightSearch = lazy(() => import('./components/FlightSearch'));
const Partners = lazy(() => import('./components/Partners'));
const PackagesPage = lazy(() => import('./pages/PackagesPage'));
const TripSharePage = lazy(() => import('./pages/TripSharePage'));
const AITripSharePage = lazy(() => import('./pages/AITripSharePage'));
const SavedEmpty = lazy(() => import('./pages/SavedEmpty'));
const ProfileEmpty = lazy(() => import('./pages/ProfileEmpty'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminInbox = lazy(() => import('./pages/AdminInbox'));
const AdminCacheAnalytics = lazy(() => import('./pages/AdminCacheAnalytics'));
const AdminAnalyticsDashboard = lazy(() => import('./pages/AdminAnalyticsDashboard'));
const AdminNewYearOffers = lazy(() => import('./pages/AdminNewYearOffers'));
const StaticContentPage = lazy(() => import('./pages/StaticContentPage'));
const DestinationPage = lazy(() => import('./pages/DestinationPage'));

// Lightweight skeleton shown while a lazy chunk loads. Matches the
// blue-gradient background of every page so the swap is invisible.
const SectionSkeleton = () => (
  <div className="min-h-[40vh] bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center" data-testid="section-skeleton">
    <div className="w-10 h-10 border-3 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
  </div>
);

function AppContent() {
  const { t, language } = useLanguage();
  const isAuthCallback = typeof window !== 'undefined' && window.location.pathname === '/auth/callback';
  const isResetPassword = typeof window !== 'undefined' && window.location.pathname === '/auth/reset-password';
  // `/shared-trip/{slug}` → canonical AI-share viral funnel route (iter54).
  // `/trip/ai/{slug}` → legacy alias, kept for backwards compatibility.
  // `/trip/{packageId}` → curated FEATURED_PACKAGES preview.
  const isAITripShare = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/shared-trip/')
    || window.location.pathname.startsWith('/trip/ai/')
  );
  const isTripShare = typeof window !== 'undefined' && window.location.pathname.startsWith('/trip/') && !isAITripShare;

  // The homepage is directly usable on the first visit. A compulsory
  // introduction overlay prevented visitors from finding flights or AI chat.

  const [activeSection, setActiveSection] = useState(() => {
    // Initialize from URL path
    const path = window.location.pathname.substring(1);
    return path || 'home';
  });
  const { user } = useAuth();

  useEffect(() => {
    if (isAuthCallback || isResetPassword || isTripShare || isAITripShare) return;
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      let mounted = true;
      
      const checkPayment = async () => {
        try {
          const result = await getPaymentStatus(sessionId);
          if (mounted && result.success && result.data?.paymentStatus === 'paid') {
            alert(language === 'bg'
              ? 'Плащането е отчетено. Резервацията се потвърждава отделно от доставчика.'
              : 'Payment recorded. Your reservation is not confirmed until the provider confirms it.');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error('Payment status check failed:', error);
        }
      };
      
      checkPayment();
      
      return () => {
        mounted = false;
      };
    }
  }, [isAuthCallback, isResetPassword, isTripShare, isAITripShare, language]);

  // Browser Back/Forward changes the URL outside React. Keep the
  // visible SPA section synchronized with the history entry.
  useEffect(() => {
    const onPopState = () => {
      setActiveSection(window.location.pathname.slice(1) || 'home');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Update URL when section changes
  useEffect(() => {
    if (isAuthCallback || isResetPassword || isTripShare || isAITripShare) return;
    const path = activeSection === 'home' ? '/' : `/${activeSection}`;
    // Retain campaign tracking and an explicitly chosen language when
    // navigating between public sections; avoid losing attribution on SPA transitions.
    const kept = new URLSearchParams(window.location.search);
    for (const key of [...kept.keys()]) {
      if (key !== 'lang' && key !== 'gclid' && key !== 'fbclid' && !key.startsWith('utm_')) kept.delete(key);
    }
    // Initial mount and Back/Forward already point to this path; never
    // insert a duplicate history entry or trap navigation loops.
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', `${path}${kept.toString() ? `?${kept.toString()}` : ''}`);
    }
    // Phase 4 · fire a `page_view` on every section transition. This is
    // what the admin dashboard's Traffic + Funnel sections consume.
    try {
      trackEvent('page_view', { section: activeSection || 'home', path });
    } catch { /* analytics is best-effort */ }
  }, [activeSection, isAuthCallback, isResetPassword, isTripShare, isAITripShare]);

  // One canonical SEO contract for section navigation. Never index account,
  // dashboard, admin, or transient shared itineraries as public landing pages.
  useEffect(() => {
    const meta = getPageMetadata(activeSection, language);
    const isMainDomain = shouldIndexHost(window.location.hostname);
    const assign = (selector, attribute, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attribute, value);
    };
    const langParam = new URLSearchParams(window.location.search).get('lang');
    const langSuffix = ['en', 'bg'].includes(langParam) ? `?lang=${langParam}` : '';
    const canonical = `https://travelaibg.com${meta.canonicalPath}${langSuffix}`;
    document.title = meta.title;
    assign('meta[name="description"]', 'content', meta.description);
    assign('meta[name="robots"]', 'content', meta.indexable && isMainDomain
      ? 'index, follow, max-image-preview:large, max-snippet:-1'
      : 'noindex, nofollow');
    assign('link[rel="canonical"]', 'href', canonical);
    assign('meta[property="og:url"]', 'content', canonical);
    assign('meta[property="og:title"]', 'content', meta.title);
    assign('meta[property="og:description"]', 'content', meta.description);
    assign('meta[name="twitter:title"]', 'content', meta.title);
    assign('meta[name="twitter:description"]', 'content', meta.description);
  }, [activeSection, language]);

  // Auth callback runs on /auth/callback before any other UI
  if (isAuthCallback) {
    return <AuthCallback />;
  }

  // Password reset deep-link from email — renders standalone page
  if (isResetPassword) {
    return <ResetPasswordPage />;
  }

  // Public shared-trip preview — viral link, no auth required
  // AI-shared trips (`/shared-trip/{slug}` canonical, `/trip/ai/{slug}` legacy)
  // get a different page than package-shared trips (`/trip/{packageId}`).
  if (isAITripShare) {
    return (
      <Suspense fallback={<SectionSkeleton />}>
        <AITripSharePage />
      </Suspense>
    );
  }
  if (isTripShare) {
    return (
      <Suspense fallback={<SectionSkeleton />}>
        <TripSharePage />
      </Suspense>
    );
  }

  const handleNavigation = (section) => {
    const previousSection = activeSection;
    setActiveSection(section);

    // Change 6 · Every internal navigation lands at the TOP of the target
    // page. React state changes don't reset window scroll, so a user who
    // clicked a discovery-deal card at the bottom of the homepage would
    // otherwise arrive on /flights already scrolled past the search form.
    if (previousSection !== section && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    // Track navigation
    trackEvent('navigation', {
      from: previousSection,
      to: section
    });
  };

  const renderSection = () => {
    // Deep-link: /destination/<key> — handle BEFORE the switch since the
    // section name carries the destination key. e.g. activeSection =
    // "destination/FCO" or "destination/lisbon".
    if (typeof activeSection === 'string' && activeSection.startsWith('destination/')) {
      const cityKey = activeSection.substring('destination/'.length);
      return <DestinationPage cityKey={cityKey} onNavigate={handleNavigation} />;
    }
    switch (activeSection) {
      case 'home':
        return (
          <div className="min-h-screen bg-white relative">
            {/* Subtle grain texture — one of the "premium magazine" cues.
                Extremely low opacity so it's felt more than seen.
                Uses an inline SVG data URI so it needs zero network hit. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.025] mix-blend-multiply"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.65'/%3E%3C/svg%3E")`,
              }}
            />
            <Hero />
            <DiscoveryDeals onNavigate={handleNavigation} />
            <section id="ai-chat" className="relative py-10 sm:py-14 bg-gradient-to-b from-white to-gray-50/50">
              <div className="max-w-5xl mx-auto px-4">
                <SectionHeader
                  kicker={t('home.section.aiKicker')}
                  title={t('home.section.aiTitle')}
                  subtitle={t('home.section.aiSubtitle')}
                  testId="home-section-ai-header"
                />
              </div>
              <AIChat onNavigate={handleNavigation} />
            </section>
            {/* iter104 — Kiwitaxi + Localrent travel-service CTAs
                 pinned to the homepage so every visitor sees them
                 regardless of chat flow. */}
            <section id="travel-services" className="relative py-6 sm:py-10 bg-white">
              <div className="max-w-5xl mx-auto px-4">
                <TravelServicesSection surface="homepage" />
              </div>
            </section>
            <section id="hidden-gem" className="relative py-8 sm:py-12">
              <div className="max-w-5xl mx-auto px-4">
                <SectionHeader
                  kicker={t('home.section.gemKicker')}
                  title={t('home.section.gemTitle')}
                  subtitle={t('home.section.gemSubtitle')}
                  testId="home-section-gem-header"
                />
              </div>
              <HiddenGemOfTheDay onNavigate={handleNavigation} />
            </section>
            <section id="deals" className="relative scroll-mt-16 py-8 sm:py-12 bg-gradient-to-b from-gray-50/60 to-white">
              <div className="max-w-6xl mx-auto px-4">
                <SectionHeader
                  kicker={t('home.section.dealsKicker')}
                  title={t('home.section.dealsTitle')}
                  subtitle={t('home.section.dealsSubtitle')}
                  testId="home-section-deals-header"
                />
              </div>
              <TravelBookingSection onNavigate={handleNavigation} />
            </section>
            <section id="rare-finds" className="relative scroll-mt-16">
              <JarvisRareFinds
                onSelect={(find) => {
                  // Route homepage rare-find clicks to Flights with the IATA
                  // pre-selected so the concierge → destination handoff feels
                  // seamless.
                  try {
                    sessionStorage.setItem('travelai:rareFindIata', find.iata);
                  } catch { /* private mode */ }
                  handleNavigation('flights');
                }}
              />
            </section>
            <HowItWorks />
            <HomepageFAQ />
          </div>
        );
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigation} />;
      case 'compare':
        return <TripComparison />;
      case 'flights':
        return <FlightSearch />;
      case 'hotels':
        return <HotelSearch />;
      case 'partners':
        return <Partners />;
      case 'packages':
        return <PackagesPage />;
      case 'saved':
        return user ? <TripComparison /> : <SavedEmpty />;
      case 'profile':
        return user ? <Dashboard onNavigate={setActiveSection} /> : <ProfileEmpty />;
      case 'contact':
        return <ContactPage onNavigate={handleNavigation} />;
      case 'admin-inbox':
      case 'admin/inbox':
        return <AdminInbox onNavigate={handleNavigation} />;
      case 'admin-cache':
      case 'admin/cache':
        return <AdminCacheAnalytics onNavigate={handleNavigation} />;
      case 'admin-new-year':
      case 'admin/new-year':
        return <AdminNewYearOffers onNavigate={handleNavigation} />;
      case 'admin-analytics':
      case 'admin/analytics':
      case 'admin':
        return <AdminAnalyticsDashboard />;
      case 'help':
      case 'faq':
      case 'privacy':
      case 'terms':
        return <StaticContentPage pageKey={activeSection} onNavigate={handleNavigation} />;
      default:
        return (
          <div className="min-h-screen bg-white">
            <Hero />
            <AIChat onNavigate={handleNavigation} />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-release-marker="travelaibg-pr4-20261027-launch-candidate">
      <Header activeSection={activeSection} setActiveSection={setActiveSection} />
      <main className="flex-1 pb-16 md:pb-0">
        <Suspense fallback={<SectionSkeleton />}>
          {renderSection()}
        </Suspense>
      </main>
      <Footer onNavigate={setActiveSection} />
      {/* Sprint E · Mobile bottom nav (hidden on ≥ md). */}
      <MobileBottomNav activeSection={activeSection} onNavigate={handleNavigation} />
      {/* V5 · Global floating AI concierge FAB — visible on every route
          except deep-link standalone pages (auth callback, reset, share). */}
      <FloatingAIWidget onNavigate={handleNavigation} activeSection={activeSection} />
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ToastProvider>
  );
}

export default App;
